import { action, computed, observable } from 'mobx';
import { RouterStore } from '@estee/elc-router';
import ConfigRepository from '../../repositories/ConfigRepository';
import TransactionRepository from '../../repositories/TransactionRepository';
import CartItem from '../../domain/entities/Cart/CartItem';
import Config from '../../domain/entities/Config';
import Store, { INVENTORY } from '../../domain/entities/Store/Store';
import Transaction, { AvailableDeliveryOptionsMap } from '../../domain/entities/Transaction/Transaction';
import { ICoordinates } from '../../interfaces/ICoordinates';
import formatPrice from '../../utils/PriceFormatUtils';
import { EDIT_CART_OPTION } from '../../views/BopisView/EditCart';
import SelectStoreUseCase from '../../use-cases/SelectStoreUseCase';
import { getCountryAbbr } from '../../utils/AddressFormUtils';
import { getPostalCodeValidation } from '../../utils/AddressValidation';
import AnalyticsRepository from '../../repositories/AnalyticsRepository';
import SessionRepository, { IUserAddress } from '../../repositories/SessionRepository/SessionRepository';
import LoadStoresService from '../../application/services/LoadStoresService';
import BopisStoresRepository from '../../repositories/BopisStoresRepository';
import Bopis from '../../domain/entities/Bopis/Bopis';
import NotificationsCollection from '../../domain/entities/Notification/NotificationsCollection';
import { BOPIS_NOTIFICATION, NOTIFICATION_GROUP, NOTIFICATION_TYPE } from '../../constants/Notifications';
import GOOGLE_MAP_STYLE from '../../constants/GoogleMapStylers';
import GenericFormStore, { IBaseFormAttributes } from '../../domain/entities/AddressFormStore/GenericFormStore';
import BopisFormData from './BopisFormData';

export type BopisContactFormStore = GenericFormStore<IBopisContactFormValues>;

interface IBopisViewControllerData {
  availableDeliveryOptions: AvailableDeliveryOptionsMap;
  formData: BopisFormData;
  brandName: string;
  regionName: string;
  cartItems: CartItem[];
  moreStoresAvailable: boolean;
  selectedStore: Store;
  showStoresWithAvailableItems: boolean;
  sortedAllStores: Store[];
  storesList: Store[];
  phoneFormatCountry: string;
  countryId: number;
  focusTo: string;
  isEditCartBtnLoading: boolean;
  noStoresFound: boolean;
  onOpenHoursToggle: (storeName: string) => void;
  editCartScreenViewEvent: (cartItems: CartItem[]) => void;
  isSampleInCart: boolean;
  userFullAddress: IUserAddress;
  userZipCode: string;
  googleMapsKey: string;
  maxRadius: string;
  showStoreNameWithAddress: boolean;
  isInvalidPostalCode: boolean;
  displayStoreCollection: boolean;
  isClickAndCollect: boolean;
  searchClicked: boolean;
  googleMapStyle: GOOGLE_MAP_STYLE;
}

interface IBopisViewControllerConfig {
  bopisStoresRepository: BopisStoresRepository;
  configRepository: ConfigRepository;
  loadStoresService: LoadStoresService;
  transactionRepository: TransactionRepository;
  sessionRepository: SessionRepository;
  stateRouter: RouterStore;
  analyticsRepository: AnalyticsRepository;
  notificationsCollection: NotificationsCollection;
}

interface ILocationQuery {
  coords?: ICoordinates;
  postalCode?: string;
}

const INCREMENT = 3;

export type IBopisContactFormValues = IBaseFormAttributes;

export default class BopisViewController {
  @observable config: Config;

  @observable private showStoresWithAvailableItems = false;

  @observable phoneFormatCountry: string;

  @observable transaction: Transaction;

  protected notificationsCollection: NotificationsCollection;

  @observable loading = false;

  @observable countryId: number;

  @observable isEditCartBtnLoading = false;

  @observable private paginationIndex = INCREMENT;

  @observable private noStoresFound = false;

  @observable private isInvalidPostalCode = false;

  @observable isClickAndCollect: boolean; 

  @observable searchClicked = false; 

  private bopisStoresRepository: BopisStoresRepository;

  private configRepository: ConfigRepository;

  private loadStoresService: LoadStoresService;

  private transactionRepository: TransactionRepository;

  private sessionRepository: SessionRepository;

  private stateRouter: RouterStore;

  private analyticsRepository: AnalyticsRepository;

  constructor(config: IBopisViewControllerConfig) {
    this.bopisStoresRepository = config.bopisStoresRepository;
    this.configRepository = config.configRepository;
    this.loadStoresService = config.loadStoresService;
    this.transactionRepository = config.transactionRepository;
    this.sessionRepository = config.sessionRepository;
    this.stateRouter = config.stateRouter;
    this.analyticsRepository = config.analyticsRepository;
    this.notificationsCollection = config.notificationsCollection;
  }

  async loaded(): Promise<void> {
    this.config = await this.configRepository.getConfig();
    this.countryId = this.config.countryId;
    this.isClickAndCollect = this.config.isClickAndCollect;
    this.transaction = await this.transactionRepository.getTransaction();
    this.phoneFormatCountry = getCountryAbbr(this.countryId);
    this.initializeBopisContactForms(this.countryId, this.transaction.bopis);
    const { bopisStoresCollection: { stores } } = this.bopisStoresRepository;
    if (!stores?.length) {
      this.noStoresFound = false;
      this.loading = true;
      await this.loadStoresService.loadBopisStoresByLocation(this.isClickAndCollect);
      this.noStoresFound = this.bopisStoresRepository.bopisStoresCollection.stores?.length === 0;
      this.loading = false;
    }

    if (this.sessionRepository.isStoreChanged()) {
      this.notificationsCollection.addNotification({
        group: NOTIFICATION_GROUP.BOPIS,
        key: BOPIS_NOTIFICATION.STORE_CHANGED,
        type: NOTIFICATION_TYPE.WARNING,
      });
      this.sessionRepository.deleteStoreChangedFlag();
    }

    // For use case where there's already a BOPIS transaction on load.
    if (this.transaction && this.transaction.bopis.doorId) {
      this.bopisStoresRepository.setIsBopisSelected(true);
      const {
        bopisStoresCollection: {
          getSelectedStore,
          setSelectedStore,
        },
        loadStore,
      } = this.bopisStoresRepository;
      // Get selected store if store is not in the list of stores near user's current location.
      const selectedStore = getSelectedStore(this.transaction.bopis.doorId);
      if (selectedStore) {
        setSelectedStore(selectedStore);
      } else {
        loadStore(this.transaction.bopis.doorId);
      }
    }
  }

  @computed
  public get data(): IBopisViewControllerData {
    const data = {
      availableDeliveryOptions: this.transaction?.availableShippingOptions,
      brandName: this.config && this.config.brandName,
      cartItems: this.transaction && this.transaction.cart.cartItems,
      countryId: this.countryId,
      displayStoreCollection: this.config && this.config.displayStoreCollection,
      editCartScreenViewEvent: this.editCartScreenViewEvent,
      focusTo: this.stateRouter.queryParams?.bopis,
      formData: this.bopisStoresRepository.bopisFormData,
      googleMapStyle: this.config?.googleMapStyle,
      googleMapsKey: this.config?.googleMapsKey,
      isClickAndCollect: this.isClickAndCollect,
      isEditCartBtnLoading: this.isEditCartBtnLoading,
      isInvalidPostalCode: this.isInvalidPostalCode,
      isSampleInCart: this.transaction && this.transaction.cart.isSampleInCart,
      maxRadius: this.bopisStoresRepository.maxRadius,
      moreStoresAvailable: this.moreStoresAvailable,
      noStoresFound: this.noStoresFound,
      onOpenHoursToggle: this.onOpenHoursToggle,
      onSelectDifferentStore: this.onSelectDifferentStore,
      phoneFormatCountry: this.phoneFormatCountry,
      regionName: this.config && this.config.regionName,
      searchClicked: this.searchClicked,
      selectedStore: this.selectedStore,
      showStoreNameWithAddress: this.config && this.config.showStoreNameWithAddress,
      showStoresWithAvailableItems: this.showStoresWithAvailableItems,
      sortedAllStores: this.bopisStoresRepository?.bopisStoresCollection?.storesSortedByDistance,
      storesList: this.filteredStores,
      userFullAddress: this.sessionRepository.userAddress,
      userZipCode: this.sessionRepository.userLocalStorageZip,
    };
    return data;
  }

  @action
  toggleAvailableItems = (): void => {
    this.showStoresWithAvailableItems = !this.showStoresWithAvailableItems;
  };

  @action
  loadStoresByLocation = async (location: ILocationQuery, storeType = 0): Promise<void> => {
    this.noStoresFound = false;
    this.isInvalidPostalCode = false;
    this.loading = true;
    const { isClickAndCollect } = this;
    const { coords, postalCode } = location;
    if (coords && coords.latitude) {
      await this.bopisStoresRepository.getStoresByGeolocation(coords, isClickAndCollect, storeType);
    }
    if (postalCode) {
      await this.bopisStoresRepository.getCoordinatesFromAddress(postalCode);
    }
    this.noStoresFound = this.bopisStoresRepository.bopisStoresCollection.stores?.length === 0;
    this.loading = false;
    this.analyticsRepository.bopisStoreSearch();
  };

  isPostalCodeValid = (zipcode: string): boolean => {
    this.noStoresFound = false;
    // TODO: Get regex pattern from schema after RCO-3159.
    const countryId = this.config && this.config.countryId;
    const zipPatternRegex = new RegExp(getPostalCodeValidation(countryId));
    this.isInvalidPostalCode = !zipPatternRegex.test(zipcode);
    return zipPatternRegex.test(zipcode);
  };

  @action
  showMoreStores = (): void => {
    this.paginationIndex += INCREMENT;
  };

  @computed
  get storesToDisplay(): Store[] {
    const { bopisStoresCollection: { storesSortedByDistance } } = this.bopisStoresRepository;
    return storesSortedByDistance?.slice(0, this.paginationIndex);
  }

  @computed
  get filteredStores(): Store[] {
    if (this.showStoresWithAvailableItems) {
      return this.storesToDisplay.filter(
        store => this.getStoreInventoryStatus(store) === INVENTORY.ALL,
      );
    }
    return this.storesToDisplay;
  }

  @computed
  private get moreStoresAvailable(): boolean {
    const { bopisStoresCollection: { stores } } = this.bopisStoresRepository;
    return stores?.length > this.storesToDisplay?.length;
  }

  @action
  public onSelectStore = (store?: Store, prevStore?: Store): void => {
    const doorId = store && store.doorId;
    this.bopisStoresRepository.bopisFormData.doorId = doorId;
    this.bopisStoresRepository.setIsBopisSelected(!!doorId);
    const useCase = new SelectStoreUseCase(store, prevStore);
    useCase.execute();
  };

  @action
  public unsetStore = (): void => {
    this.onSelectStore(null, this.selectedStore);
    if (this.transaction?.isClickAndCollectTrans) {
      this.setSearchClicked(true);
    }
  };

  @action
  public onSelectDifferentStore = (store?: Store): void => {
    this.onSelectStore(store, this.selectedStore);
  };

  @action
  public editCartScreenViewEvent = (cartItems: CartItem[]): void => {
    this.analyticsRepository.bopisEditCartScreenViewEvent(cartItems);
  };

  @action
  public onEditStore = async (
    option: EDIT_CART_OPTION, storeId: string, location?: ICoordinates,
  ): Promise<void> => {
    const {
      bopisStoresCollection: {
        getSelectedStore,
        setSelectedStore,
      },
    } = this.bopisStoresRepository;
    const store = getSelectedStore(storeId);

    if (option === EDIT_CART_OPTION.CHANGE_STORE) {
      this.onSelectStore(store);
    } else if (option === EDIT_CART_OPTION.REMOVE_ITEMS) {
      this.analyticsRepository.itemsRemovedFromUnavailableItemsScreen(this.selectedStore);
      this.isEditCartBtnLoading = true;
      const { doorId } = this.selectedStore;
      await this.bopisStoresRepository.removeUnavailableItems(doorId);
      await this.transactionRepository.loadTransaction();
      setSelectedStore(this.selectedStore);
      this.onSelectStore(this.selectedStore);
      // Reload stores to get updated inventory information.
      this.loading = true;
      if (location) {
        this.loadStoresByLocation({ coords: location });
      } else {
        await this.loadStoresService.loadBopisStoresByLocation(this.isClickAndCollect);
      }
      this.loading = false;
    } else {
      this.analyticsRepository.deliveryMethodChangedFromUnavailableItemsScreen();
    }
  };

  @computed
  private get selectedStore(): Store {
    const { getSelectedStore, selectedStore } = this.bopisStoresRepository.bopisStoresCollection;
    if (this.transaction && this.transaction.bopis.doorId) {
      return (
        getSelectedStore(this.transaction.bopis.doorId)
        || selectedStore
      );
    }
    if (this.bopisStoresRepository.bopisFormData.doorId) {
      return getSelectedStore(this.bopisStoresRepository.bopisFormData.doorId);
    }
    // TODO RCO-8572: refactor to get store from trans after RCO-8513 is completed
    if (this.transaction?.isClickAndCollectTrans && selectedStore) {
      return selectedStore;
    }
    return undefined;
  }

  @computed
  get cartItemsUnavailable(): CartItem[] {
    return this.transaction
      && this.transaction.cart
      && this.transaction.cart.cartItems.filter(cartItem => {
        const item = this.selectedStore
          && this.selectedStore.inventory.find(inv => {
            return (inv.skuBaseId === cartItem.skuBaseId || inv.GWPSaleableSku === cartItem.skuBaseId);
          });
        const isUnavailableItem = item && !item.isAvailable;
        const isNotSampleItem = !cartItem.isSampleItem;
        const isGWPofferItem = cartItem.isGWPOfferItem;
        return isUnavailableItem && (isNotSampleItem || isGWPofferItem);
      });
  }

  @computed
  get shouldShowEditCart(): boolean {
    return this.cartItemsUnavailable && Boolean(this.cartItemsUnavailable.length);
  }

  @computed
  get isOnlyGWPUnavailable(): boolean {
    return this.cartItemsUnavailable
    && (this.cartItemsUnavailable.every(item => item.isBopisGWPItem)
      || this.cartItemsUnavailable.every(item => item.isCourierGWPItem));
  }

  private initializeBopisContactForms = async (countryId: number, bopisData: Bopis): Promise<void> => {
    const { bopisFormData } = this.bopisStoresRepository;

    bopisFormData.countryId = countryId;
    bopisFormData.init(bopisData);
  };

  getPrice = (price: number): string => this.config && formatPrice(this.config, price);

  @computed
  public get shouldShowStoreList(): boolean {
    const { bopisStoresCollection: { stores } } = this.bopisStoresRepository;
    if (stores?.length > 0 || this.filteredStores?.length > 0) {
      return true;
    }
    return false;
  }

  @action
  public onOpenHoursToggle = (storeName: string): void => {
    this.analyticsRepository.bopisStoreOpenHoursToggle(storeName);
  };

  getStoreInventoryStatus = (store: Store): INVENTORY => {
    const { inventory, itemsAvailable } = store;
    const notAvailableItems = inventory.filter(({ isAvailable }) => !isAvailable);
    const sampleItems = this.transaction?.cart?.cartItems.filter(item => {
      return item.isSampleItem && !item.isGWPOfferItem;
    });
    const isAllNotAvailableSamples = notAvailableItems.length === sampleItems?.length;

    switch (true) {
      case (itemsAvailable === INVENTORY.PARTIAL) && isAllNotAvailableSamples:
        return INVENTORY.ALL;
      case itemsAvailable === INVENTORY.PARTIAL:
        return INVENTORY.PARTIAL;
      case itemsAvailable === INVENTORY.NONE:
        return INVENTORY.NONE;
      case itemsAvailable === INVENTORY.ALL:
        return INVENTORY.ALL;
      default:
        return null;
    }
  };

  @action
  loadBopisStoresByLocation = async (storeType = 0): Promise<void> => {
    this.loading = true;
    await this.loadStoresService.loadBopisStoresByLocation(this.isClickAndCollect, storeType);
    this.loading = false;
  };

  @action
  handleGoogleSearchChange = (): void => {
    this.sessionRepository.deleteUserLocalStorageZip();
    this.sessionRepository.deleteUserLocalStorageAddress();
  };

  @action
  setUserLocalStorageZip = (zipcode: string): void => {
    this.sessionRepository.setUserLocalStorageZip(zipcode);
  };

  @action
  setSearchClicked = (searchClicked: boolean): void => {
    this.searchClicked = searchClicked;
  };
}
